// Front view of the anatomical body map (MUSCLE_BODY_MAP_PLAN.md, Task 4.2).
// React-free / JSX-free -- scripts/validateMuscleMap.ts imports this module
// directly (hazard #7).
//
// ============================================================================
// SHARED SKELETON SPEC  --  all three views are drawn against this table
// ----------------------------------------------------------------------------
// viewBox="0 0 220 520", centerline x = 110.
//
// PROPORTION: 7.8 heads tall, inside the 7.5 (realistic) to 8 (idealized)
// canon. Head height h = 64 (crown y = 10 -> chin y = 74); the figure runs
// 10 -> 512, so 501/64 = 7.83 h. Widths are quoted in head units too, because
// that is what makes a figure read as a body rather than as a mannequin.
//
//   Landmark                  |  y  | Front/back x range      | width in h
//   --------------------------|-----|-------------------------|------------
//   crown                     |  10 | head 87-133             | 0.72 h
//   chin                      |  74 | jaw ~ 94-126            | 0.50 h
//   neck base (C7)            |  88 | neck 91-129             | 0.59 h
//   acromion (shoulder top)   | 100 | -                       | -
//   deltoid widest            | 130 | 35-185                  | 2.34 h
//   armpit apex               | 130 | torso 67-153            | 1.34 h
//   nipple line               | 140 | -                       | -
//   xiphoid (bottom of pecs)  | 172 | torso 69-151            | 1.28 h
//   navel                     | 205 | -                       | -
//   waist (narrowest)         | 208 | 74-146                  | 1.13 h
//   iliac crest               | 242 | 64-156                  | 1.44 h
//   pubis / crotch            | 260 | -                       | -
//   hip widest (trochanter)   | 270 | 61-159                  | 1.53 h
//   mid-thigh                 | 310 | each thigh ~ 39         | 0.61 h
//   knee center               | 364 | knee 72-100 / 120-148   | 0.44 h
//   calf widest               | 418 | calf ~ 41               | 0.64 h
//   ankle                     | 481 | ankle 77-95             | 0.28 h
//   ball of the foot          | 502 | foot 71-98              | -
//   toe pads                  | 512 | five toes, big toe last | -
//
// Shoulder-to-waist is 150:72 = 2.08 (the V-taper) and hip-to-shoulder is
// 98:150 = 0.65. Those two ratios carry the whole silhouette. The JAW is
// deliberately wider than the neck it meets (94-126 against 91-129 just below
// it): when it is narrower, the head's own closed outline reads as a chin
// floating inside the neck and the figure looks like a ball on a stalk.
//
// ARMS hang in anatomical position (palms forward), slightly abducted, so the
// armpit is a closing V rather than a gap: the arm's inner edge and the ribcage
// MEET at the armpit apex (67, 130) and diverge below it. The deltoid cap spans
// y 100-155 and converges on the deltoid tuberosity at roughly HALF the
// humerus (y 153) -- the landmark that stops a deltoid being drawn as a pad
// swallowing the whole upper arm. Elbow y 196, wrist y 257, fingertips
// y 298-312, level with mid-thigh. The hand is 46 long against a 64 head, and
// its palm and its fingers are the same length; both are real proportions and
// both are what stop a hand reading as a paddle.
//
// HANDS AND FEET are traced into the silhouette rather than mittened: four
// fingers plus a thumb per hand, five toes per foot. At this scale a finger is
// ~5 units wide -- small, but the stroke BodyMap.tsx puts on the outline is
// what makes them read, and they are most of what makes the figure look drawn
// rather than diagrammed.
//
// SIDE VIEW (regionsSide.ts) uses the same y landmarks. It faces screen-left,
// so anterior is low x and posterior is high x; its own header carries the
// full profile table.
// ============================================================================
//
// Authoring notes:
// - Only the screen-LEFT half of each paired muscle is hand-drawn; pairRegions
//   derives the "-r" twin with mirrorPath, so the halves cannot drift apart.
//   "-l" is the left half of the *image*, not a claim about the subject's side;
//   both halves shade identically today (D-BM-8).
// - The silhouette is likewise one hand-drawn half chain closed by mirrorClose.
// - Regions TILE rather than float: adjacent muscles share a border and the
//   skin-colored silhouette shows through only at the face, neck, palms,
//   insteps and joints. Muscles drawn as islands read as stickers.
// - Several muscles are multiple subpaths inside one `d` (the rectus abdominis'
//   four bellies, the serratus' digitations). mirrorPath handles repeated "M"
//   commands, so those mirror correctly.
// - Where two muscles genuinely overlap on the body, the superficial one is
//   listed LATER so it paints (and is clicked) on top: the serratus over the
//   oblique, the vastus medialis over the adductors' lower end.
// - transverse-abdominis is the one muscle with no surface of its own
//   (deep: true); rather than stacking it on the rectus columns (which would
//   make one of the two unclickable) it is drawn as the transverse
//   sub-umbilical band its fibers actually run along.
// - The taxonomy expansion (EXERCISE_LIBRARY_AND_MUSCLE_TAXONOMY_PLAN.md
//   Task 1.4) re-cut four existing paths rather than stacking the new muscles
//   on top of them, because this file tiles: the transverse abdominis gave up
//   its lowest 5 units to the pelvic floor, the adductor mass gave up its
//   medial third to the gracilis, the tibialis anterior gave up its lateral
//   third to the peroneals, and the medial head of the gastrocnemius gave up
//   2 units to the tibialis posterior. Each one was wider than the muscle it
//   names; none lost enough area to stop reading.

import type { BodyRegion } from "./geometry.js";
import { VIEWBOX, centerRegion, mirrorClose, pairRegions } from "./geometry.js";

export const FRONT_VIEWBOX = VIEWBOX;

/**
 * Screen-left half of the body silhouette: neck -> trapezius slope -> deltoid
 * -> arm -> hand (thumb, then four fingers) -> back up the inner arm -> armpit
 * -> ribcage -> waist -> hip -> outer leg -> foot (five toes) -> inner leg ->
 * crotch, which sits ON the centerline so the mirrored return leg joins
 * seamlessly.
 */
const SILHOUETTE_HALF = [
  "M 99 62", // starts inside the jaw, so mirrorClose's closing line is hidden
  "C 97 71 93 80 91 88", // neck
  "C 81 92 67 96 55 100", // trapezius slope out to the acromion
  "C 45 104 37 112 36 124", // deltoid cap, the widest point of the figure
  "C 35 136 35 146 36 155", // upper arm, outer edge
  "C 37 169 39 182 41 194", // down to the elbow
  "C 38 202 35 210 34 220", // brachioradialis bulge below the elbow
  "C 34 232 35 244 37 256", // forearm tapering into the wrist
  // --- hand: thumb first, then index / middle / ring / little finger ---
  "C 35 261 33 264 32 268", // base of the thumb
  "C 29 272 27 279 29 284", // thumb, outer edge to the tip
  "C 32 288 34 285 34 280", // around the tip and back up into the web
  "C 32 289 32 295 33 300", // index finger, outer edge
  "C 34 306 37 307 38 303", // index fingertip
  "C 39 298 38 296 38 293", // index finger, inner edge
  "C 39 298 39 304 40 308", // middle finger, outer edge
  "C 42 312 45 312 45 307", // middle fingertip -- the longest
  "C 45 302 45 297 45 293", // middle finger, inner edge
  "C 46 298 46 302 47 305", // ring finger, outer edge
  "C 48 309 51 308 51 304", // ring fingertip
  "C 51 300 51 296 51 292", // ring finger, inner edge
  "C 52 295 52 297 53 300", // little finger, outer edge
  "C 54 304 57 303 57 298", // little fingertip
  "C 56 289 55 279 54 270", // back up the hand's inner edge
  "C 53 264 53 260 53 256", // wrist, inner edge
  "C 54 244 55 230 57 216", // forearm, inner edge
  "C 59 206 61 199 62 194", // elbow crease
  "C 63 178 64 160 66 147", // upper arm, inner edge
  "C 67 139 67 133 67 130", // armpit apex -- arm and ribcage meet here
  "C 68 142 68 156 69 171", // ribcage down to the xiphoid
  "C 71 185 73 198 74 208", // the waist, narrowest point of the trunk
  "C 70 218 66 230 64 242", // flare out over the iliac crest
  "C 62 252 61 260 61 270", // greater trochanter, widest point of the hips
  "C 62 286 63 300 65 316", // outer thigh
  "C 68 334 70 350 72 364", // down to the knee
  "C 70 378 68 391 66 404", // knee into the calf
  "C 63 418 63 433 66 447", // gastrocnemius belly
  "C 70 461 74 471 77 481", // down to the ankle
  // --- foot: five toes, little toe first, big toe last ---
  "C 75 490 73 497 72 502", // outside of the foot down to the ball
  "C 71 507 75 508 76 504", // little toe
  "C 76 509 80 510 81 505", // fourth toe
  "C 81 510 85 511 86 505", // third toe
  "C 86 511 91 512 91 505", // second toe
  "C 92 512 98 511 97 502", // big toe
  "C 97 494 96 488 95 481", // inside of the foot back up to the ankle
  "C 97 469 100 457 102 445", // inner calf
  "C 104 431 104 417 103 405", // medial gastrocnemius head, which hangs lower
  "C 102 391 101 377 100 364", // inner knee
  "C 100 348 101 331 103 315", // inner thigh
  "C 105 299 107 283 109 270", // adductor mass
  "C 110 265 110 262 110 260", // crotch, on the centerline
].join(" ");

/**
 * The closed body silhouette. Exported so regionsBack.ts can reuse the exact
 * same shape -- the two views must register pixel-for-pixel when toggled.
 */
export const BODY_SILHOUETTE = mirrorClose(SILHOUETTE_HALF);

/**
 * Head: a smooth ovoid, broad through the cranium and tapering to a rounded
 * jaw, with no ears and no features -- the anatomy-chart convention, and what
 * keeps the eye on the musculature rather than on a face. Shared by the front
 * and back views for the same reason as the silhouette.
 */
export const HEAD_OUTLINE =
  "M 110 10 C 122 10 132 20 133 34 C 133 46 130 57 126 65 C 123 70 117 74 110 74 C 103 74 97 70 94 65 C 90 57 87 46 87 34 C 88 20 98 10 110 10 Z";

/** Non-interactive silhouette drawn beneath the regions and never highlighted. */
export const FRONT_OUTLINE: string[] = [BODY_SILHOUETTE, HEAD_OUTLINE];

export const FRONT_REGIONS: BodyRegion[] = [
  // --- neck ------------------------------------------------------------------
  // The three neck regions tile the front of the neck between the jaw (y 73)
  // and the clavicles (y 91), inside the trapezius' medial border. Listed
  // before traps-upper, which owns the strip out to the silhouette edge.
  // Deep cervical flexors: deep, in front of the cervical spine, so they get
  // the narrow band right off the midline -- the narrowest of the three.
  ...pairRegions(
    "deep-cervical-flexors",
    "M 103 78 C 104 82 105 86 106 90 C 107 91 109 91 109 90 C 109 86 109 82 109 78 C 108 77 104 77 103 78 Z",
  ),
  // Scalenes: deep, and the only part of them a front view could ever show is
  // the wedge behind the SCM that widens toward the first rib.
  ...pairRegions(
    "scalenes",
    "M 96 79 C 97 83 98 87 100 91 C 98 92 96 92 95 91 C 95 87 95 83 96 79 Z",
  ),
  // Sternocleidomastoid: the diagonal strap every front-on neck actually
  // shows, running from behind the ear down to the sternoclavicular joint --
  // lateral at the top, medial at the bottom. Drawing it vertically is what
  // makes a neck read as a column instead of a neck. Listed last of the three
  // because it is the superficial one.
  ...pairRegions(
    "sternocleidomastoid",
    "M 96 73 C 96 79 98 85 100 91 C 102 92 104 92 106 91 C 104 85 102 79 102 73 C 100 72 98 72 96 73 Z",
  ),

  // --- shoulder girdle -------------------------------------------------------
  // Upper trapezius. It originates on the OCCIPITAL BONE, so it runs up the
  // side of the neck to just below the skull before sweeping out to the
  // acromion; a trapezius that starts at the shoulder line is the commonest
  // way a front view ends up looking like a mannequin. Its lower border is the
  // clavicle, shared with the pec's and the deltoid's upper edge.
  ...pairRegions(
    "traps-upper",
    "M 96 74 C 95 80 93 86 91 91 C 80 95 68 98 56 101 C 61 112 72 118 84 116 C 92 112 95 94 96 74 Z",
  ),
  // The deltoid is an inverted triangle: broad along the clavicle and acromion,
  // converging on the deltoid tuberosity at half the humerus (48, 153). Split
  // down its length into two lobes meeting on the acromion-to-tuberosity line.
  ...pairRegions(
    "delt-lateral",
    "M 57 101 C 46 105 37 113 36 125 C 35 137 35 147 37 154 C 41 158 46 158 48 153 C 50 136 54 116 57 101 Z",
  ),
  ...pairRegions(
    "delt-anterior",
    "M 58 101 C 64 104 68 112 68 124 C 68 136 65 147 59 154 C 55 157 50 157 48 153 C 50 136 54 116 58 101 Z",
  ),

  // --- chest -----------------------------------------------------------------
  // Both pec heads are FANS, not slabs: a broad origin on the clavicle, sternum
  // and costal cartilages converging on one narrow insertion at the humerus.
  // Drawing them as rounded rectangles is what makes a chest read as a bib.
  // Clavicular head: the medial half of the clavicle, out to that insertion.
  ...pairRegions(
    "pec-clavicular",
    "M 106 93 C 93 94 79 100 71 110 C 69 114 68 121 69 128 C 76 122 84 118 92 116 C 98 115 102 114 106 114 Z",
  ),
  // Sternal head: origin down the whole sternum and the cartilages of ribs 1-6,
  // lower border sweeping up and laterally into the same insertion -- which is
  // what gives a real chest its diagonal lower edge instead of a flat one.
  ...pairRegions(
    "pec-sternal",
    "M 106 117 C 94 118 83 122 74 129 C 71 132 69 136 70 141 C 77 151 85 160 93 166 C 98 170 102 172 106 172 Z",
  ),

  // --- trunk -----------------------------------------------------------------
  // Rectus abdominis: one column per side, divided by the three tendinous
  // intersections a real muscle has -- at the xiphoid, at the umbilicus and
  // midway between -- which leaves three short bellies above the navel and one
  // longer one below. Four equal blocks is the giveaway of a diagram.
  ...pairRegions(
    "rectus-abdominis",
    "M 108 172 C 99 172 92 176 90 184 C 91 187 94 189 97 190 C 102 189 105 189 108 189 Z " +
      "M 108 192 C 99 192 93 196 91 203 C 92 206 95 208 98 209 C 102 208 105 208 108 208 Z " +
      "M 108 211 C 100 211 94 215 93 221 C 94 224 96 226 99 227 C 103 226 105 226 108 226 Z " +
      "M 108 229 C 101 229 96 233 95 239 C 95 242 97 244 99 245 C 103 244 105 244 108 244 Z",
  ),
  // Obliques: the whole flank from the lower ribs to the iliac crest, lateral
  // border on the silhouette. The serratus digitations are painted over their
  // upper-lateral corner further down this list, exactly as they interdigitate.
  ...pairRegions(
    "obliques",
    "M 90 178 C 84 180 78 186 74 196 C 72 206 72 214 75 223 C 72 232 70 241 69 250 C 76 256 85 252 89 244 C 91 224 91 200 90 178 Z",
  ),
  // Serratus anterior: the finger-like digitations on the ribs, running as a
  // diagonal arc down and forward from under the armpit -- listed after the
  // obliques so they read (and click) as the superficial layer they are.
  ...pairRegions(
    "serratus-anterior",
    "M 70 152 C 75 156 80 163 82 170 C 79 175 74 172 71 166 C 69 161 68 155 70 152 Z " +
      "M 73 172 C 78 176 83 183 84 190 C 81 195 76 192 74 186 C 72 181 71 175 73 172 Z " +
      "M 76 192 C 81 196 85 202 86 208 C 83 213 79 210 77 205 C 75 200 74 195 76 192 Z",
  ),
  // Transverse abdominis has no surface of its own; drawn as the sub-umbilical
  // band its fibers actually run across, below the rectus columns (deep: true).
  centerRegion(
    "transverse-abdominis",
    "M 92 244 C 104 240 116 240 128 244 C 128 250 124 254 117 255 C 112 256 108 256 103 255 C 96 254 92 250 92 244 Z",
  ),
  // Pelvic floor: the hammock slung across the base of the pelvis, just below
  // the transverse abdominis it braces with. Unpaired and centered -- it is
  // one continuous sling, not a left and a right half.
  centerRegion(
    "pelvic-floor",
    "M 100 257 C 105 255 115 255 120 257 C 120 261 116 265 110 266 C 104 265 100 261 100 257 Z",
  ),

  // --- arms ------------------------------------------------------------------
  // Biceps takes the inner two-thirds of the upper arm below the deltoid
  // tuberosity; brachialis is the strip that shows lateral to it.
  ...pairRegions(
    "biceps-brachii",
    "M 65 152 C 57 158 51 170 50 184 C 50 192 53 198 57 200 C 61 196 64 181 65 167 C 66 157 66 153 65 152 Z",
  ),
  ...pairRegions(
    "brachialis",
    "M 47 156 C 42 164 39 175 39 186 C 39 193 41 198 44 198 C 47 190 49 176 49 165 C 49 159 48 156 47 156 Z",
  ),
  // The forearm splits front-on the way it really does: the flexor group on the
  // medial (inner) side, the extensor group and brachioradialis forming the
  // lateral edge. Both bellies sit HIGH, just below the elbow, and taper to
  // tendon at the wrist -- a forearm drawn as an even sausage is the giveaway.
  ...pairRegions(
    "forearm-flexors",
    "M 58 202 C 52 210 48 223 47 237 C 46 248 48 256 51 259 C 54 255 55 243 55 230 C 56 215 58 205 58 202 Z",
  ),
  ...pairRegions(
    "forearm-extensors",
    "M 47 202 C 41 209 36 221 34 235 C 33 246 34 254 37 258 C 41 254 43 242 44 229 C 45 214 47 205 47 202 Z",
  ),

  // --- hips and thighs -------------------------------------------------------
  // TFL: the wedge on the point of the hip, above the vastus lateralis.
  ...pairRegions(
    "tfl",
    "M 72 236 C 66 242 63 251 64 262 C 68 270 75 271 80 265 C 81 253 78 242 72 236 Z",
  ),
  // Psoas major: deep, and the only honest way to draw it front-on is as the
  // oblique band its fibers run along -- off the lumbar spine at the navel,
  // out and down to the lesser trochanter. Listed after the rectus and the
  // obliques it lies behind, so it stays visible and clickable (the same
  // trade transverse-abdominis makes).
  ...pairRegions(
    "psoas-major",
    "M 93 222 C 91 234 89 247 87 261 C 89 264 92 264 94 261 C 96 247 98 234 99 223 C 97 220 95 220 93 222 Z",
  ),
  // Iliacus: the fan filling the iliac fossa, meeting the psoas at the
  // trochanter -- which is why the two share their lowest few units, exactly
  // as the iliopsoas tendon they form does.
  ...pairRegions(
    "iliacus",
    "M 82 249 C 79 255 79 262 82 267 C 86 270 91 268 93 263 C 94 256 91 251 87 247 C 85 246 83 247 82 249 Z",
  ),
  // Vastus intermedius is deep -- listed first among the quads so the other
  // three paint over it. What is left is the widening wedge between the
  // vastus lateralis and the rectus femoris, which is the one place on a
  // front view the intermedius comes anywhere near the surface.
  ...pairRegions(
    "vastus-intermedius",
    "M 85 285 C 84 295 83 310 82 330 C 81 342 80 350 80 356 C 84 359 88 358 89 354 C 88 344 87 330 86 312 C 86 298 86 289 85 285 Z",
  ),
  // Quadriceps fill the thigh. Vastus lateralis is the largest of the three and
  // its belly sits HIGH; rectus femoris runs straight down the middle from the
  // hip to the patella; vastus medialis' belly sits LOW -- the teardrop just
  // above the inner knee. Those three belly heights are most of what makes a
  // thigh look like a thigh rather than three parallel stripes.
  ...pairRegions(
    "vastus-lateralis",
    "M 85 266 C 73 272 65 290 63 312 C 62 333 66 350 72 360 C 78 358 81 345 82 327 C 83 303 85 282 85 266 Z",
  ),
  ...pairRegions(
    "rectus-femoris",
    "M 98 264 C 91 267 86 282 85 305 C 85 327 87 345 91 357 C 96 356 98 346 97 330 C 98 306 98 282 98 264 Z",
  ),
  // The adductor mass (magnus / longus / brevis) now stops short of the
  // midline: its medial third is the gracilis' lane. It still tapers to its
  // insertion around y 319, which is where the group's short heads end.
  ...pairRegions(
    "hip-adductors",
    "M 100 261 C 98 270 97 282 97 296 C 97 306 98 314 101 319 C 102 313 102 302 102 289 C 102 276 102 267 102 263 C 102 261 101 260 100 261 Z",
  ),
  // Listed after the adductors: the two genuinely overlap on the inner thigh
  // and the vastus is the superficial one there.
  ...pairRegions(
    "vastus-medialis",
    "M 98 306 C 94 313 92 326 92 340 C 92 352 95 360 99 361 C 101 357 102 344 101 330 C 100 316 99 309 98 306 Z",
  ),
  // Gracilis: the one adductor that reaches past the knee, so it is drawn as
  // the long narrow strap hugging the midline all the way down -- much
  // longer and much thinner than the mass it sits medial to. Listed after the
  // vastus medialis because on the inner thigh the gracilis is the
  // superficial one.
  ...pairRegions(
    "gracilis",
    "M 105 265 C 103 283 102 300 100 318 C 99 332 98 342 98 348 C 99 350 100 350 100 347 C 100 340 101 330 102 318 C 104 300 107 283 109 266 C 108 263 106 263 105 265 Z",
  ),
  // Sartorius: the longest muscle in the body, and on a front view it is a
  // single diagonal strap from the ASIS to the medial knee. Listed last of
  // the thigh regions because it crosses all of them and is superficial to
  // every one.
  ...pairRegions(
    "sartorius",
    "M 70 249 C 74 275 82 305 93 355 C 96 358 99 356 98 353 C 89 304 81 274 76 248 C 74 246 71 246 70 249 Z",
  ),

  // --- lower leg -------------------------------------------------------------
  // Tibialis anterior sits LATERAL to the subcutaneous tibial crest and its
  // tendon crosses back toward the midline at the ankle, so the strip leans in
  // at the bottom rather than running straight down the middle of the shin.
  ...pairRegions(
    "tibialis-anterior",
    "M 87 378 C 82 386 78 405 77 428 C 77 448 79 464 82 474 C 86 478 89 472 89 458 C 90 432 90 399 87 378 Z",
  ),
  // Peroneals: the lateral compartment, the column between the tibialis
  // anterior and the edge of the calf. It is the one muscle group a front
  // view shows that ankle-eversion work actually trains, so it must not be
  // folded into the shin.
  ...pairRegions(
    "peroneals",
    "M 79 386 C 74 396 72 412 72 432 C 72 450 74 464 77 474 C 79 476 81 475 81 472 C 78 460 77 446 77 430 C 78 410 80 394 82 388 C 82 385 80 384 79 386 Z",
  ),
  // Tibialis posterior: deep, behind the tibia, so what it gets is the narrow
  // strip along the medial border of the shin bone -- where it is palpated
  // and where its tendon runs down to the arch.
  ...pairRegions(
    "tibialis-posterior",
    "M 91 394 C 90 410 89 432 90 452 C 90 462 91 468 92 472 C 94 473 95 470 94 465 C 93 450 93 430 94 410 C 94 402 94 396 93 393 C 92 391 91 391 91 394 Z",
  ),
  // Gastrocnemius from the front. The medial head is big enough to show past
  // the tibia on the inside of the leg, and a thinner strip of the lateral head
  // shows on the outside -- which is why a front view of a lower leg is not
  // just a shin.
  ...pairRegions(
    "gastrocnemius",
    "M 94 382 C 98 390 103 406 104 424 C 104 440 101 454 97 462 C 96 458 95 442 95 424 C 95 404 95 388 94 382 Z " +
      "M 69 388 C 66 398 64 410 65 424 C 65 436 67 445 70 450 C 72 446 72 434 72 421 C 71 407 70 395 69 388 Z",
  ),
  // Soleus emerging below the gastrocnemius on both sides, running into the
  // achilles.
  ...pairRegions(
    "soleus",
    "M 95 464 C 98 468 99 473 98 478 C 95 480 92 479 91 475 C 90 470 92 466 95 464 Z " +
      "M 71 452 C 74 457 75 464 74 470 C 71 472 68 470 68 465 C 68 459 69 454 71 452 Z",
  ),
  // Plantar intrinsics: the arch band inside the existing foot outline, kept
  // clear of the toe pads below it. Small, but this is the only place the
  // short-foot and toe-spread work a PT sheet prescribes has to land.
  ...pairRegions(
    "plantar-intrinsics",
    "M 76 494 C 82 492 89 492 94 494 C 95 499 92 503 87 504 C 82 505 78 503 76 501 C 75 499 75 496 76 494 Z",
  ),
];
